import { useState } from "react";
import { Badge, Spinner } from "react-bootstrap";
import { ChevronDown, ChevronUp } from "react-bootstrap-icons";

import { useGroups } from "../../hooks/useGroups";

import { getGroupBadgeVariant } from "./groupBadge";
import { compareGroupNames } from "./groupOrder";

export type GroupFilterMode = "or" | "and";

interface GroupFilterPanelProps {
  selectedGroups: string[];
  onChange: (groups: string[]) => void;
  mode: GroupFilterMode;
  onModeChange: (mode: GroupFilterMode) => void;
}

const GroupFilterPanel: React.FC<GroupFilterPanelProps> = ({
  selectedGroups,
  onChange,
  mode,
  onModeChange,
}) => {
  const { groups, loading } = useGroups();
  const [expanded, setExpanded] = useState(false);

  const toggleGroup = (groupName: string) => {
    if (selectedGroups.includes(groupName)) {
      onChange(selectedGroups.filter((g) => g !== groupName));
    } else {
      onChange([...selectedGroups, groupName]);
    }
  };

  const sortedGroups = [...groups].sort((a, b) => compareGroupNames(a.group_name, b.group_name));

  return (
    <div className="mb-3">
      <div className="d-flex align-items-center gap-3">
        <button
          type="button"
          className="d-inline-flex align-items-center gap-1 bg-transparent border-0 p-0 text-dark"
          aria-expanded={expanded}
          onClick={() => {
            setExpanded((prev) => !prev);
          }}
        >
          {expanded ? <ChevronUp /> : <ChevronDown />}
          Filter by Group{selectedGroups.length > 0 ? ` (${String(selectedGroups.length)})` : ""}
        </button>

        {selectedGroups.length > 0 && (
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={() => {
              onChange([]);
            }}
          >
            Clear Filters
          </button>
        )}
      </div>

      {expanded && (
        <div className="mt-2">
          <div className="d-flex align-items-center gap-2 mb-2">
            <span className="small text-muted">Match:</span>
            <div className="d-inline-flex border rounded overflow-hidden">
              <button
                type="button"
                className={`px-2 py-1 border-0 small ${mode === "or" ? "bg-dark text-white" : "bg-light text-dark"}`}
                aria-pressed={mode === "or"}
                onClick={() => {
                  onModeChange("or");
                }}
              >
                Any (OR)
              </button>
              <button
                type="button"
                className={`px-2 py-1 border-0 small ${mode === "and" ? "bg-dark text-white" : "bg-light text-dark"}`}
                aria-pressed={mode === "and"}
                onClick={() => {
                  onModeChange("and");
                }}
              >
                All (AND)
              </button>
            </div>
          </div>

          <div className="d-flex flex-wrap gap-2">
            {loading && <Spinner animation="border" size="sm" />}
            {!loading &&
              sortedGroups.map((group) => {
                const isSelected = selectedGroups.includes(group.group_name);
                return (
                  <Badge
                    key={group.group_name}
                    bg={isSelected ? getGroupBadgeVariant(group.group_name) : "light"}
                    text={isSelected ? undefined : "dark"}
                    role="button"
                    tabIndex={0}
                    className="border"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      toggleGroup(group.group_name);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleGroup(group.group_name);
                      }
                    }}
                  >
                    {group.group_name}
                  </Badge>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupFilterPanel;
